import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { NAV, BRAND } from "@/data/content";
import { motion, AnimatePresence } from "framer-motion";
import { MegaMenu } from "./MegaMenu";

export const Nav = ({ onOpenDemo }) => {
    const [scrolled, setScrolled] = useState(false);
    const [mobile, setMobile] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [openMobileSection, setOpenMobileSection] = useState(null);
    const location = useLocation();
    const closeTimer = useRef(null);

    useEffect(() => {
        let lastY = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 20);
            if (Math.abs(y - lastY) > 4) setOpenDropdown(null);
            lastY = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobile(false);
        setOpenDropdown(null);
        setOpenMobileSection(null);
    }, [location]);

    const openMenu = (label) => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setOpenDropdown(label);
    };
    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpenDropdown(null), 140);
    };

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all ${
                scrolled
                    ? `${location.pathname === "/" ? "gleap-nav-dark" : "gleap-nav"} py-3`
                    : "bg-transparent py-5"
            }`}
            data-testid="site-nav"
        >
            <div className="container-x flex items-center justify-between gap-6">
                <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
                    <img
                        src={BRAND.logo}
                        alt="NyraAI"
                        className="logo-mark h-11 w-auto"
                        style={{ transform: "scale(2.2)", transformOrigin: "left center" }}
                    />
                </Link>

                <nav className="nav-capsule hidden lg:flex items-center gap-2 relative">
                    {NAV.map((item) => {
                        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                        if (!hasChildren) {
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    className={({ isActive }) =>
                                        `px-4 py-2 text-sm transition-colors rounded-full ${
                                            isActive
                                                ? "text-slate-900 font-medium"
                                                : "text-slate-600 hover:text-slate-900"
                                        }`
                                    }
                                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                                >
                                    {item.label}
                                </NavLink>
                            );
                        }
                        const isOpen = openDropdown === item.label;
                        const isActive = location.pathname.startsWith(item.to);
                        return (
                            <div
                                key={item.label}
                                className="relative"
                                onMouseEnter={() => openMenu(item.label)}
                                onMouseLeave={scheduleClose}
                            >
                                <button
                                    onClick={() =>
                                        setOpenDropdown((v) => (v === item.label ? null : item.label))
                                    }
                                    className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 rounded-full transition-colors ${
                                        isActive
                                            ? "text-slate-900 font-medium"
                                            : "text-slate-600 hover:text-slate-900"
                                    }`}
                                    data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
                                    aria-expanded={isOpen}
                                    aria-haspopup="menu"
                                >
                                    {item.label}
                                    <motion.i
                                        animate={{ rotate: isOpen ? 180 : 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="fa-solid fa-chevron-down text-[10px] opacity-70"
                                    />
                                </button>

                                <AnimatePresence>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 8 }}
                                            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                                            className="fixed left-4 right-4 top-[80px] z-50 pointer-events-none"
                                            data-testid={`nav-dropdown-panel-${item.label.toLowerCase()}`}
                                            role="menu"
                                            onMouseEnter={() => openMenu(item.label)}
                                            onMouseLeave={scheduleClose}
                                        >
                                            <div className="max-w-[1180px] mx-auto pointer-events-auto">
                                                <MegaMenu
                                                    item={item}
                                                    onClose={() => setOpenDropdown(null)}
                                                    onOpenDemo={onOpenDemo}
                                                />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        className="pill-dark hidden sm:inline-flex"
                        onClick={onOpenDemo}
                        data-testid="nav-book-demo"
                        style={{ padding: "10px 20px", fontSize: 13 }}
                    >
                        Book a demo
                    </button>
                    <button
                        aria-label="Toggle menu"
                        className="nav-mobile-btn lg:hidden w-10 h-10 grid place-items-center rounded-full border border-slate-200 bg-white"
                        onClick={() => setMobile((v) => !v)}
                        data-testid="nav-mobile-toggle"
                    >
                        <i className={`fa-solid ${mobile ? "fa-xmark" : "fa-bars"} text-slate-900`} />
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {mobile && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="lg:hidden container-x mt-3"
                    >
                        <div className="gleap-card p-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
                            {NAV.map((item) => {
                                const hasChildren =
                                    Array.isArray(item.children) && item.children.length > 0;
                                const isOpen = openMobileSection === item.label;
                                if (!hasChildren) {
                                    return (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            end={item.to === "/"}
                                            className={({ isActive }) =>
                                                `text-sm px-3 py-2 rounded-lg ${
                                                    isActive
                                                        ? "text-slate-900 bg-slate-100 font-medium"
                                                        : "text-slate-700 hover:bg-slate-50"
                                                }`
                                            }
                                        >
                                            {item.label}
                                        </NavLink>
                                    );
                                }
                                return (
                                    <div key={item.label}>
                                        <button
                                            className="w-full flex items-center justify-between text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-50"
                                            onClick={() =>
                                                setOpenMobileSection((v) =>
                                                    v === item.label ? null : item.label,
                                                )
                                            }
                                            data-testid={`nav-mobile-dropdown-${item.label.toLowerCase()}`}
                                        >
                                            <span>{item.label}</span>
                                            <motion.i
                                                animate={{ rotate: isOpen ? 180 : 0 }}
                                                className="fa-solid fa-chevron-down text-[10px] text-slate-500"
                                            />
                                        </button>
                                        <AnimatePresence>
                                            {isOpen && (
                                                <motion.ul
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden ml-2 border-l border-slate-200 pl-2"
                                                >
                                                    <li>
                                                        <Link
                                                            to={item.to}
                                                            className="block text-xs text-slate-500 uppercase tracking-[0.2em] px-3 py-2"
                                                        >
                                                            View all →
                                                        </Link>
                                                    </li>
                                                    {item.children.map((c) => (
                                                        <li key={c.to}>
                                                            <Link
                                                                to={c.to}
                                                                className="flex items-center gap-3 text-sm text-slate-700 px-3 py-2 rounded-lg hover:bg-slate-50"
                                                            >
                                                                <i
                                                                    className={`${c.icon} text-slate-500 text-xs w-4`}
                                                                />
                                                                {c.label}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </motion.ul>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                            <button
                                className="pill-dark mt-3 justify-center"
                                onClick={() => {
                                    setMobile(false);
                                    onOpenDemo?.();
                                }}
                                data-testid="nav-mobile-book-demo"
                            >
                                Book a demo
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Nav;
